<template>
  <section class="mt-5 mb-5">
    <div class="container">
      <!-- Header -->
      <div class="row justify-content-center">
        <div class="col-md-8">
          <div class="page-bar py-3">
            <div class="container">
              <div class="d-flex align-items-center">
                <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                  <i class="fas fa-chevron-left"></i>
                </BackButton>
                <div class="page-title flex-grow-1">Hired Vendors</div>
              </div>
            </div>
          </div>

          <!-- Location picker -->
          <LocationPicker @location-selected="handleLocationSelected" />
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center my-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Finding vendors near you...</p>
      </div>

      <!-- Error -->
      <div v-if="error" class="alert alert-danger mt-4 d-flex align-items-center">
        <div class="flex-grow-1">No vendors Found in This Location, Click on Retry or Change Location</div>
        <button class="btn btn-sm btn-outline-danger py-2 px-3 ml-2" @click="retryLoading">
          Retry
        </button>
      </div>

      <!-- Vendors -->
      <div v-if="vendors.length && !error" class="row mt-3 justify-content-center">
        <div class="col-12 col-md-8 mb-3">
          <p>Select the vendor and choose the categories the vendor will serve for this event.</p>

          <div
            class="card shadow-sm mb-3"
            v-for="vendor in vendors"
            :key="vendor.id"
          >
            <div class="card-body d-flex align-items-start">
              <img
                :src="vendor.s_user_img || fallbackLogo"
                alt="Vendor Logo"
                class="rounded mr-3"
                style="width:60px;height:60px;object-fit:cover"
              >
              <div class="flex-grow-1 pl-2 min-w-0">
                <div class="d-flex align-items-start">
                  <h5 class="mb-0 text-truncate mr-2" :title="vendor.displayName">
                    {{ vendor.displayName }}
                  </h5> 
                </div>

                <p v-if="vendor.fullAddress" class="text-muted mb-1 small">
                  <span class="text-truncate d-inline-block w-100">
                    <i class="fas fa-map-marker-alt mr-1 text-primary"></i> {{ vendor.fullAddress }}
                  </span>
                </p>

                <p v-if="vendor.context_categories" class="text-muted mb-1 small">
                  <span class="text-truncate d-inline-block w-100">
                    <i class="fas fa-shapes mr-1 text-primary"></i> {{ vendor.context_categories }}
                  </span>
                </p>
              </div>

              <button
                class="btn btn-outline-primary btn-sm px-3 py-1"
                @click="openCategoryModal(vendor)"
              >
                <i class="fas fa-plus-circle mr-2"></i>Add
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-if="!loading && !vendors.length && selectedLocation && !error" class="row mt-4">
        <div class="col-12 text-center py-5">
          <img v-no-right-click src="https://dummyimage.com/200x200/222/222.png" alt="No vendors" style="max-width:200px" class="mb-3">
          <h5>No vendors found in this area</h5>
          <p class="text-muted">Try adjusting your location or search radius</p>
          <button class="btn btn-outline-primary mt-2" @click="resetLocation">
            Change Location
          </button>
        </div>
      </div>

      <!-- Category Selection Modal (headless, Vue-controlled) -->
      <div v-if="showModal">
        <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true" aria-labelledby="categoryModalTitle">
          <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
              <div class="modal-header">
                <h5 id="categoryModalTitle" class="modal-title">Choose Categories</h5>
                <button type="button" class="close" aria-label="Close" @click="closeModal">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div class="modal-body">
                <p>
                  Choose the categories that this vendor will serve for your event. When you click <strong>Add Vendor</strong>, the vendor will be added to your Hired Vendors list.
                </p>

                <div class="form-group" v-if="selectedVendor">
                  <label class="font-weight-bold">{{ selectedVendor.displayName }}</label>
                </div>

                <div v-if="categoryOptions.length" class="categories-list">
                  <div
                    v-for="opt in categoryOptions"
                    :key="opt.id"
                    class="form-check mb-2"
                  >
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :value="opt.id"
                      :id="'category-' + opt.id"
                      v-model="selectedCategories"
                    >
                    <label class="form-check-label" :for="'category-' + opt.id">
                      {{ opt.name }}
                    </label>
                  </div>
                </div>

                <div v-else class="text-muted small">
                  No categories available for this vendor.
                </div>
              </div>

              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="addHiredVendor"
                  :disabled="selectedCategories.length === 0 || addingVendor"
                >
                  <span v-if="addingVendor" class="spinner-border spinner-border-sm mr-2"></span>
                  {{ addingVendor ? 'Adding...' : 'Add Vendor' }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- Backdrop -->
        <div class="modal-backdrop fade show" @click="closeOnBackdrop"></div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import LocationPicker from '@/components/common/LocationPicker.vue'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'
import BackButton from '@/components/common/BackButton.vue'

const route = useRoute()
const session = useSessionStore()

/** event_id from /detail/vendors/add-vendor/:event_id (or trailing numeric segment) */
const deriveEventId = (r) => {
  const cand = [
    r.params?.event_id, r.params?.id,
    r.query?.event_id,  r.query?.id
  ].find(v => v != null && String(v).trim() !== '')
  if (cand) return String(cand)
  const lastSeg = (r.path || r.fullPath || '').split('?')[0].split('/').filter(Boolean).pop()
  return lastSeg && /^\d+$/.test(lastSeg) ? lastSeg : null
}
const eventId = ref(deriveEventId(route))
watch(() => route.fullPath, () => { eventId.value = deriveEventId(route) })

const vendors = ref([])
const loading = ref(false)
const selectedLocation = ref(null)
const error = ref(null)
const fallbackLogo = 'https://dummyimage.com/60x60/f3f4f6/9ca3af.png&text=V'

/* Modal state (Vue controlled, no jQuery) */
const showModal = ref(false)
const selectedVendor = ref(null)
const categoryOptions = ref([])   // [{id, name}]
const selectedCategories = ref([])
const addingVendor = ref(false)

/* UI helpers */
const composeAddress = (v) => {
  if (v.s_address) return v.s_address
  const parts = []
  if (v.s_city) parts.push(v.s_city)
  if (v.s_state) parts.push(v.s_state)
  if (v.s_country) parts.push(v.s_country)
  return parts.join(', ')
}

/* Modal open/close */
const openCategoryModal = (vendor) => {
  selectedVendor.value = vendor
  selectedCategories.value = []

  // Build category options from vendor fields
  // IDs: vendor.contextual_category (comma IDs)
  // Names: vendor.context_categories (comma names)
  const ids = String(vendor.contextual_category || '').split(',').map(s => s.trim()).filter(Boolean)
  const names = String(vendor.context_categories || '').split(',').map(s => s.trim()).filter(Boolean)

  const opts = []
  const max = Math.max(ids.length, names.length)
  for (let i = 0; i < max; i++) {
    const id = ids[i] || `cat-${i}`
    const name = names[i] || names[0] || 'Category'
    opts.push({ id, name })
  }
  categoryOptions.value = opts

  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
}
const closeOnBackdrop = (e) => {
  // click backdrop to close
  if (e?.target?.classList?.contains('modal-backdrop')) {
    closeModal()
  } else {
    closeModal()
  }
}

/* Add hired vendor */
const addHiredVendor = async () => {
  if (!selectedVendor.value || selectedCategories.value.length === 0) return
  try {
    addingVendor.value = true

    const fd = new FormData()
    fd.append('event_id', eventId.value || '')
    fd.append('user_id', session.userId)
    fd.append('vendor_id', selectedVendor.value.s_id)              // actual vendor ID
    fd.append('categories', selectedCategories.value.join(','))    // CSV of contextual category IDs

    const res = await api.addHiredVendors(fd)
    const payload = res?.data ?? res
    if (payload?.status === 'ok' || payload?.status === true) {
      closeModal()
      alert('Vendor added successfully to hired vendors!')
    } else {
      throw new Error(payload?.message || 'Failed to add vendor')
    }
  } catch (err) {
    console.error('Error adding hired vendor:', err)
    alert('Failed to add vendor. Please try again.')
  } finally {
    addingVendor.value = false
  }
}

/* Load nearby vendors */
const handleLocationSelected = async (location) => {
  try {
    loading.value = true
    error.value = null
    selectedLocation.value = location

    const fd = new FormData()
    fd.append('lat', location.lat)
    fd.append('long', location.lng)
    fd.append('id', session.userId)
    fd.append('event_id', eventId.value || '') // pass event_id from URL

    const res = await api.getNearByVendors(fd)
    const payload = res?.data ?? res

    if (payload?.status !== 'ok') {
      throw new Error('API returned non-ok status')
    }

    const list = Array.isArray(payload.list) ? payload.list : []
    vendors.value = list.map((v, i) => {
      const displayName = v.s_business || 'Vendor'
      const fullAddress = composeAddress(v)
      return {
        id: v.s_id || `nv-${i}`,
        s_id: v.s_id,
        s_user_img: v.s_user_img || '',
        displayName,
        fullAddress,
        distance: v.distance || '',
        context_categories: v.context_categories || '',
        contextual_category: v.contextual_category || '',
        ...v
      }
    })
     if (selectedLocation.value) handleLocationSelected(selectedLocation.value)
  } catch (err) {
    console.error('Error fetching vendors:', err)
    error.value = 'Failed to load vendors. Please try again.'
    vendors.value = []
  } finally {
    loading.value = false
  }
}

/* Helpers */
const resetLocation = () => {
  selectedLocation.value = null
  vendors.value = []
  error.value = null
}
const retryLoading = () => {
  if (selectedLocation.value) handleLocationSelected(selectedLocation.value)
}
</script>

<style scoped>
.card { transition: transform .2s, box-shadow .2s; border-radius: 10px; overflow: hidden; }
.card:hover { transform: translateY(-5px); box-shadow: 0 10px 20px rgba(0,0,0,.1); }

.min-w-0 { min-width: 0; }

.page-bar{
  position: sticky;
  top: 0;
  z-index: 1000;
  background: #fff;
  border-bottom: 1px solid rgba(0,0,0,.06);
}
.page-title{ font-weight: 600; font-size: 1.05rem; }

/* Modal scroll area */
.categories-list {
  max-height: 300px;
  overflow-y: auto;
}

/* Headless modal helpers to mimic Bootstrap behavior */
.modal.d-block { display: block; }
.modal-backdrop { position: fixed; top:0; left:0; right:0; bottom:0; background: rgba(0,0,0,.5); }
</style>
